/**
 * Retrieve Presentation Image Tool
 *
 * Extracts a specific image from a slide shape.
 * Uses the /context/resolve endpoint to get image data from the indexed deck.
 */
import { ToolDescription, safeParseToolParams } from "../../../../base/toolDescription";
import { ToolBase } from "../../../../base/toolBase";
import { backendClient } from "../../../../../../services/communication/backendClient";
import { getContextResolver } from "../../../../../core/contextResolver";
import { getOrCreatedocumentId } from "../../../../../../services/documentIdManager";
import { simpleHash, findExistingAsset, registerContentHash } from "../../utils/deduplicationCache";
import { resizeImageForVision } from "../../utils/imageResize";
import { RetrievePresentationImageParamsSchema, } from "./retrievePresentationImage.schema";
// ═══════════════════════════════════════════════════════════════════════════
// DECK ID RESOLUTION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Resolve deck ID from various input formats.
 */
function unwrapQuotedSelector(value) {
    const trimmed = value.trim();
    if (trimmed.length >= 2) {
        const quoteWrapped = (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
            (trimmed.startsWith("'") && trimmed.endsWith("'"));
        if (quoteWrapped) {
            return trimmed.slice(1, -1).trim();
        }
    }
    if (trimmed.length >= 4) {
        const escapedQuoteWrapped = (trimmed.startsWith('\\"') && trimmed.endsWith('\\"')) ||
            (trimmed.startsWith("\\'") && trimmed.endsWith("\\'"));
        if (escapedQuoteWrapped) {
            return trimmed.slice(2, -2).trim();
        }
    }
    return trimmed;
}
function normalizeDocumentSelector(value) {
    if (typeof value === "number") {
        return value;
    }
    let normalized = value.trim();
    for (let i = 0; i < 3; i++) {
        const unwrapped = unwrapQuotedSelector(normalized);
        if (unwrapped === normalized)
            break;
        normalized = unwrapped;
    }
    if (/^\d+$/.test(normalized)) {
        return Number.parseInt(normalized, 10);
    }
    if (normalized.toUpperCase() === "CURRENT")
        return "CURRENT";
    if (normalized.toUpperCase() === "ALL")
        return "ALL";
    return normalized;
}
async function resolvedocumentId(documentId) {
    const normalizedSelector = normalizeDocumentSelector(documentId);
    if (normalizedSelector !== documentId) {
        console.warn(`[RetrievePresentationImage] Normalized documentId from "${String(documentId)}" to "${String(normalizedSelector)}"`);
    }
    if (normalizedSelector === "CURRENT") {
        try {
            const currentdocumentId = await getOrCreatedocumentId();
            console.log(`[RetrievePresentationImage] Resolved CURRENT to deck: ${currentdocumentId}`);
            return currentdocumentId;
        }
        catch (error) {
            console.error(`[RetrievePresentationImage] Failed to get current deck ID:`, error);
            throw new Error('Unable to determine current presentation deck ID');
        }
    }
    if (typeof normalizedSelector === 'number') {
        console.log(`[RetrievePresentationImage] Using deck index: ${normalizedSelector}`);
        return `deck_index_${normalizedSelector}`;
    }
    return normalizedSelector;
}
// ═══════════════════════════════════════════════════════════════════════════
// RETRIEVE PRESENTATION IMAGE TOOL
// ═══════════════════════════════════════════════════════════════════════════
// System prompt fallback for Keywords AI
const RETRIEVE_PRESENTATION_IMAGE_SYSTEM_PROMPT = `### retrievePresentationImage
Extract a specific image from a presentation slide.

**When to use:**
- After searchPresentationImages found desired image
- To get actual image for insertion into slides

**Call AFTER searchPresentationImages** to get slideId and shapeId.

\`\`\`json
{
  "documentId": "CURRENT",
  "slideId": "slide_257",
  "shapeId": "5",
  "requirementId": "C"
}
\`\`\`

Returns: deckimg_xxx asset ID for use with insertArtifacts.`;
/**
 * Tool for extracting images from presentation slides.
 *
 * Processing:
 * 1. Extracts image from slide shape data via Python backend
 * 2. Uses existing description or generates new one
 * 3. Saves to ContextResolver (deduplicates if already stored)
 */
export class RetrievePresentationImageTool extends ToolBase {
    constructor() {
        super();
        this.name = "retrievePresentationImage";
        // System prompt description
        this.systemPromptDescription = RETRIEVE_PRESENTATION_IMAGE_SYSTEM_PROMPT;
        // Streaming operation fields
        this.fieldsToCheck = [
            { name: 'uiDescription', type: 'string', list_status: false, order: 0 },
            { name: 'documentId', type: 'string', list_status: false, order: 1 },
            { name: 'slideId', type: 'string', list_status: false, order: 2 },
            { name: 'shapeId', type: 'string', list_status: false, order: 3 },
            { name: 'requirementId', type: 'string', list_status: false, order: 4 },
        ];
        this.listsToCheck = [];
        this.description = new ToolDescription("retrievePresentationImage", `Extract a specific image from a presentation slide.

**Purpose:** Retrieve an image found via searchPresentationImages.

**Parameters:**
- documentId: Deck containing the image
- slideId: Slide ID from search result
- shapeId: Shape ID containing the image
- requirementId: The requirement this asset fulfills

**Example:**
\`\`\`json
{
  "documentId": "CURRENT",
  "slideId": "slide_257",
  "shapeId": "shape_42",
  "requirementId": "C"
}
\`\`\`

**Processing:**
1. Extracts image from slide shape data
2. Uses existing description or generates new one
3. Saves to Asset DB with deduplication

**Returns:**
- assetId: Composite ID (e.g., "deck_abc|slide_257|shape_42")
- note: Description
- slideId, shapeId: Source location
- alreadyExists: True if this exact asset was already stored`, RetrievePresentationImageParamsSchema);
    }
    async execute(parameters) {
        const parseResult = safeParseToolParams(RetrievePresentationImageParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }
        const params = parseResult.data;
        const documentIdStr = typeof params.documentId === 'number'
            ? `index ${params.documentId}`
            : params.documentId;
        console.log(`[RetrievePresentationImageTool] Extracting from deck ${documentIdStr}, slide ${params.slideId}, shape ${params.shapeId}`);
        try {
            // Resolve deck ID
            const resolveddocumentId = await resolvedocumentId(params.documentId);
            console.log(`[RetrievePresentationImageTool] Resolved deck ID: ${resolveddocumentId}`);
            // Build context ID for shape resolution: "deck_id|slide_id|shape_id"
            const contextId = `${resolveddocumentId}|${params.slideId}|${params.shapeId}`;
            console.log(`[RetrievePresentationImageTool] Resolving context: ${contextId}`);
            // Use /context/resolve endpoint to get image data from indexed deck
            const resolved = await backendClient.resolveContext(contextId);
            if (!resolved.success || !resolved.isResolved) {
                console.error(`[RetrievePresentationImageTool] Failed to resolve context:`, resolved.error);
                return {
                    success: false,
                    assetId: '',
                    requirementId: params.requirementId,
                    note: '',
                    slideId: params.slideId,
                    shapeId: params.shapeId,
                    metadata: {
                        type: "asset",
                        source: "presentation",
                        documentId: resolveddocumentId,
                        slideId: params.slideId,
                        shapeId: params.shapeId,
                    },
                    alreadyExists: false,
                    error: resolved.error || 'Failed to resolve shape context',
                };
            }
            // Check if we got image data
            if (!resolved.imageBase64) {
                console.warn(`[RetrievePresentationImageTool] No image data for shape ${params.shapeId}`);
                return {
                    success: false,
                    assetId: '',
                    requirementId: params.requirementId,
                    note: '',
                    slideId: params.slideId,
                    shapeId: params.shapeId,
                    metadata: {
                        type: "asset",
                        source: "presentation",
                        documentId: resolveddocumentId,
                        slideId: params.slideId,
                        shapeId: params.shapeId,
                        shapeType: resolved.shapeType,
                    },
                    alreadyExists: false,
                    error: `Shape ${params.shapeId} does not contain image data. Shape type: ${resolved.shapeType || 'unknown'}`,
                };
            }
            const base64 = resolved.imageBase64;
            const description = resolved.description || '';
            const mimeType = 'image/png';
            // Check for deduplication using content hash
            const hash = simpleHash(base64);
            const contextResolver = getContextResolver();
            // Use the composite ID as the asset ID (document-inherited ID)
            // Format: "deck_id|slide_id|shape_id"
            const assetId = contextId;
            // Check if this exact image already exists (by content hash)
            const existingAssetId = findExistingAsset(hash);
            if (existingAssetId) {
                console.log(`[RetrievePresentationImageTool] ♻️ Deduplicated: ${existingAssetId}`);
                return {
                    success: true,
                    assetId: existingAssetId,
                    requirementId: params.requirementId,
                    note: description || `Image from ${params.slideId}, already stored`,
                    slideId: params.slideId,
                    shapeId: params.shapeId,
                    metadata: {
                        type: "asset",
                        source: "presentation",
                        documentId: resolveddocumentId,
                        slideId: params.slideId,
                        shapeId: params.shapeId,
                        mimeType: mimeType,
                    },
                    alreadyExists: true,
                };
            }
            // Convert (if SVG) and resize image if needed for Anthropic's vision API
            const resized = await resizeImageForVision(base64, mimeType);
            if (resized.wasConverted) {
                console.log(`[RetrievePresentationImageTool] SVG converted to PNG for vision model compatibility`);
            }
            if (resized.wasResized) {
                console.log(`[RetrievePresentationImageTool] Image resized for vision model compatibility`);
            }
            // Store in ContextResolver with image data (using potentially converted/resized version)
            contextResolver.storeImage(assetId, resized.base64, resized.mimeType, {
                source: 'presentation',
                documentId: resolveddocumentId,
                slideId: params.slideId,
                shapeId: params.shapeId,
                contentHash: hash,
                description: description,
                originalFormat: mimeType,
                wasConverted: resized.wasConverted,
                wasResized: resized.wasResized,
            });
            // Register hash for future deduplication
            registerContentHash(hash, assetId);
            const note = description || `Image from ${params.slideId}, ${params.shapeId}`;
            console.log(`[RetrievePresentationImageTool] ✅ Retrieved asset: ${assetId}`);
            return {
                success: true,
                assetId,
                requirementId: params.requirementId,
                note,
                slideId: params.slideId,
                shapeId: params.shapeId,
                metadata: {
                    type: "asset",
                    source: "presentation",
                    documentId: resolveddocumentId,
                    slideId: params.slideId,
                    shapeId: params.shapeId,
                    mimeType: resized.mimeType,
                },
                alreadyExists: false,
            };
        }
        catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error("[RetrievePresentationImageTool] ❌ Error:", error);
            return {
                success: false,
                assetId: '',
                requirementId: params.requirementId,
                note: '',
                slideId: params.slideId,
                shapeId: params.shapeId,
                metadata: {
                    type: "asset",
                    source: "presentation",
                    documentId: typeof params.documentId === 'string' ? params.documentId : `deck_${params.documentId}`,
                    slideId: params.slideId,
                    shapeId: params.shapeId,
                },
                alreadyExists: false,
                error: errorMsg,
            };
        }
    }
}
